import { Component } from 'react';
import { Dimensions, Text, View } from 'react-native';

import IconFailure from '../../../assets/svg/IconFailure';
import IconSuccess from '../../../assets/svg/IconSuccess';
import IconWarning from '../../../assets/svg/IconWarning';
import { peacock } from '../../../constants/color';
import { PAY_VALUE_BY_NAME } from '../../../constants/payby';
import { translate } from '../../../i18n';
import { TransactionStatus } from '../../../types/Common';
import { PaymentData } from '../../../types/TransactionDetail';
import isExists from '../../../utils/isExists';
import RetryBtnGroup from '../RetryBtnGroup';
import PaymentItem from './PaymentItem';
import styles from './PaymentSection.style';

const { fontScale } = Dimensions.get('window');

interface PropsPaymentData extends PaymentData {
  transaction_no: string;
  transaction_status: string;
  transaction_hint: string;
  created_at: number;
  PAY_NAME: string;
}

interface Props {
  paymentData: PropsPaymentData;
  transactionID: number;
  transactionStatus: TransactionStatus;
  patchTransaction: Function;
  setDirectPayModalState: Function;
  // 訂單結果頁面返回到特定頁面
  // 會因為要顯示錯誤訊息使用 getDerivedStateFromProps 做state 的更新，發生一些狀況
  // 頁面: 訂單結果頁 -> 購物車
  // 因此透過 isFocused（源自 page 層透過 withIsFocused 傳入的)
  isEntered: boolean;
  renderSpec: any;
  fetchTransactionDetail: Function;
}

function shouldHandleError(payBy) {
  return (
    payBy === PAY_VALUE_BY_NAME.CREDIT_CARD ||
    payBy === PAY_VALUE_BY_NAME.CARD_LINK ||
    payBy === PAY_VALUE_BY_NAME.DIRECT_PAY_PRIME ||
    payBy === PAY_VALUE_BY_NAME.DIRECT_PAY_TOKEN ||
    payBy === PAY_VALUE_BY_NAME.APPLE_PAY ||
    payBy === PAY_VALUE_BY_NAME.AFTEE ||
    payBy === PAY_VALUE_BY_NAME.LINE_PAY
  );
}

function isResultPage(renderSpec) {
  // 因為有在背景更新的狀況和需求，所以不能使用 Actions.currentScene
  return renderSpec.general[0].name === 'payment_text';
}

export default class PaymentSection extends Component<Props> {
  renderPayment() {
    const { paymentData, renderSpec, transactionStatus } = this.props;

    if (!paymentData) {
      return null;
    }
    const configs = [
      ...(renderSpec.general || []),
      ...(renderSpec[paymentData.PAY_NAME] || []),
    ];
    const customValueStyle = {
      payment_limit: [styles.dateValueText],
    };
    const customValueLayoutStyle = {
      account_number: [{ flexDirection: 'column' }],
    };
    const customHintStyle = {
      cvs_code: [styles.dataValueHint, styles.lightGray],
      account_number: [styles.accountTips],
    };

    return configs.reduce((result, config) => {
      const {
        name,
        getValue,
        checkValue,
        hintName,
        getHint,
        checkHint,
        valueTemplate,
        hintTemplate,
      } = config;
      const theValue = getValue(paymentData, name);
      const isValidate = checkValue(theValue, paymentData, transactionStatus);
      const theHint = hintName ? getHint(paymentData, hintName) : null;
      const isHintValidate = theHint ? checkHint(theHint) : false;

      if (isValidate) {
        const valueLayoutStyles = [
          styles.dataValueLayout,
          ...(customValueLayoutStyle[name] || []),
        ];

        const shouldRefresh =
          name === 'account_number' &&
          paymentData.pay_by === PAY_VALUE_BY_NAME.SINOPAC_VIRTUAL_ACCOUNT &&
          theValue === '';

        const valueStyles = [
          styles.dataValueText,
          ...(customValueStyle[name] || []),
          ...(config.customValueStyle || []),
        ];

        const hintStyles = customHintStyle[name] || [];

        const valueTempParams = {
          currencySymbol: paymentData.currency.symbol,
        };

        const valueTemp = (value, overrideParams) =>
          valueTemplate(config.formatted(value), valueStyles, {
            ...valueTempParams,
            ...overrideParams,
          });

        const hintTemp =
          !shouldRefresh && isHintValidate && hintTemplate(theHint, hintStyles);

        const separatorTemp =
          isExists(config.separatorTemplate) &&
          config.isShowSeparator(paymentData) &&
          config.separatorTemplate(styles.separator);

        result.push(
          <PaymentItem
            key={name}
            name={name}
            theValue={theValue}
            valueTemplate={valueTemp}
            hintTemplate={hintTemp}
            valueLayoutStyles={valueLayoutStyles}
            separatorTemplate={separatorTemp}
            transactionID={this.props.transactionID}
            shouldRefresh={shouldRefresh}
            fetchTransactionDetail={this.props.fetchTransactionDetail}
          />
        );
      }

      return result;
    }, []);
  }

  renderCreditCardResult() {
    const { paymentData, renderSpec } = this.props;

    if (
      !paymentData ||
      (!isResultPage(renderSpec) && !paymentData.error_message.length)
    ) {
      return null;
    }
    const { error_message, is_paid } = paymentData;

    return is_paid ? (
      <View style={styles.creditMsg}>
        <IconSuccess size={24 * fontScale} />
        <Text style={[styles.creditMsgText, styles.creditSuccess]}>
          {translate('orderDetail-credit-success')}
        </Text>
      </View>
    ) : (
      <View style={styles.creditMsg}>
        <IconFailure size={24 * fontScale} />
        <Text style={[styles.creditMsgText, styles.creditFailure]}>
          {isResultPage(renderSpec)
            ? translate('orderDetail-credit-failure', {
                msg: error_message || '',
              })
            : error_message || ''}
        </Text>
      </View>
    );
  }

  render() {
    const {
      paymentData,
      transactionID,
      patchTransaction,
      setDirectPayModalState,
    } = this.props;

    if (!paymentData) {
      return null;
    }
    const { pay_by, is_paid, transaction_status, transaction_hint } =
      paymentData;

    return (
      <View style={[styles.container]}>
        <View style={[styles.paymentSection]}>
          {isExists(transaction_hint) && !!transaction_hint.length && (
            <View style={styles.limitHint}>
              <IconWarning size={18 * fontScale} fillColor={peacock} />
              <Text style={styles.limitHintText} numberOfLines={2}>
                {transaction_hint}
              </Text>
            </View>
          )}
          {this.renderPayment()}
          {shouldHandleError(pay_by) && this.renderCreditCardResult()}
          {shouldHandleError(pay_by) &&
            transaction_status !== 'expired' &&
            !is_paid && (
              <RetryBtnGroup
                patchTransaction={patchTransaction}
                paymentData={paymentData}
                setDirectPayModalState={setDirectPayModalState}
                transactionID={transactionID}
              />
            )}
        </View>
      </View>
    );
  }
}
